import { useState, useEffect, useCallback } from 'react'
import { PAYMENT_TYPES } from '@vend/business-components'
import { ApiRetailerPaymentType } from '@vend/types'
import { trackError } from '@vend/utilities'
import {
  isEnabledPaymentTypeForOutlet,
  isIpadOnly,
} from '../../../../functions/payment-types/payment-types'
import { paymentTypesStore } from '../../../../factories/database/payment-types-store'
import {
  isReturnedLSPayESale,
  preparePaymentTypesForLSpayEcomRefund,
} from '../../../../functions/payment-types/lspayEcom'
import { KLARNA_PAYMENT_TYPES } from '../../../payments/integrated-payment/integrated-payment-types.constant'
import { registerManager } from '../../../../ng-compat/managers'
import { getFeatureFromCache } from '../../../../util/feature-cache'
import { useAppSelector } from '../../../../state/hooks'
import { selectCurrentSale } from '../../../../state/slices/currentSale/currentSaleSlice'
import { useTrainingModeManager } from '../../../../ng-compat/hooks'

const paymentNotAvailable = (paymentType: ApiRetailerPaymentType) => {
  const activeOutletId = registerManager.getActiveOutlet()?.id
  const paymentTypeControlsFeatureEnabled = getFeatureFromCache(
    'payment_type_controls'
  )
  if (paymentType.disabled) {
    return true
  }

  if (paymentType.deleted_at) {
    return true
  }

  if (isIpadOnly(paymentType.payment_type)) {
    return true
  }

  if (paymentTypeControlsFeatureEnabled) {
    // If the payment type is restricted by outlets, and the active outlet is not
    // one of these, filter the payment type
    if (!isEnabledPaymentTypeForOutlet(paymentType, activeOutletId)) {
      return true
    }
  }
  return false
}

/**
 * Returns a filtered collection of payment types that can be displayed on the pay screen
 */
const filterPaymentTypesForDisplay = (
  paymentTypes: ApiRetailerPaymentType[],
  isLSPayEcomRefund: boolean,
  isTrainingModeEnabled: boolean
): ApiRetailerPaymentType[] => {
  // Payment types to exclude from the list of primary payment methods on the pay screen
  const EXCLUDED_PAYMENT_TYPES = [
    PAYMENT_TYPES.AFTER_PAY, // Afterpay is no longer supported. All afterpay retailer payment types should be deleted. Just to be safe we always exclude it from the pay screen
    PAYMENT_TYPES.LOYALTY,
    PAYMENT_TYPES.XERO,
    PAYMENT_TYPES.STORE_CREDIT,
    PAYMENT_TYPES.ROUNDING,
    PAYMENT_TYPES.CURRENCY_ADJUST,
  ]

  if (isTrainingModeEnabled) {
    EXCLUDED_PAYMENT_TYPES.push(PAYMENT_TYPES.GIFT_CARD)
    EXCLUDED_PAYMENT_TYPES.push(PAYMENT_TYPES.WORLDPAY)
    EXCLUDED_PAYMENT_TYPES.push(PAYMENT_TYPES.WORLDPAY_TRIPOS)
    EXCLUDED_PAYMENT_TYPES.push(PAYMENT_TYPES.BIGCOMMERCE_GIFT_CERT)
    EXCLUDED_PAYMENT_TYPES.push(PAYMENT_TYPES.LSPAY)
    EXCLUDED_PAYMENT_TYPES.push(...KLARNA_PAYMENT_TYPES)
  }

  if (!isLSPayEcomRefund) {
    EXCLUDED_PAYMENT_TYPES.push(PAYMENT_TYPES.LIGHTSPEED_ECOM)
  }
  let isLSPayFiltered = false

  return paymentTypes.filter(paymentType => {
    if (EXCLUDED_PAYMENT_TYPES.includes(paymentType.type_id)) {
      return false
    }

    // Hide integrated payment types from training mode
    if (isTrainingModeEnabled && paymentType.config && paymentType.config.url) {
      return false
    }

    const paymentIsNotAvailable = paymentNotAvailable(paymentType)
    if (paymentIsNotAvailable) {
      // If the payment type is not available, filter it out
      return false
    }

    // Filter out only the first LSPay payment type from the payment types list.
    // Users may have multiple LSPay payment types, but we'll only show the first one at the top
    // of the list. All additional LSPay payment types will still be included in the payment types list
    // without any modifications.
    if (paymentType.type_id === PAYMENT_TYPES.LSPAY && !isLSPayFiltered) {
      isLSPayFiltered = true
      return false
    }

    return true
  })
}

/**
 * Define system payment types from all payment types
 */
const defineSystemPaymentTypes = (paymentTypes: ApiRetailerPaymentType[]) => {
  const systemTypes: {
    roundingPaymentType?: ApiRetailerPaymentType
    currencyAdjustmentPaymentType?: ApiRetailerPaymentType
    storeCreditPaymentType?: ApiRetailerPaymentType
    loyaltyPaymentType?: ApiRetailerPaymentType
    lspayPaymentType?: ApiRetailerPaymentType
  } = {}

  let filteredLSPay = false
  for (const paymentType of paymentTypes) {
    if (paymentType.deleted_at) {
      continue
    }

    if (paymentType.type_id === PAYMENT_TYPES.STORE_CREDIT) {
      systemTypes.storeCreditPaymentType = paymentType
    }

    if (paymentType.type_id === PAYMENT_TYPES.ROUNDING) {
      systemTypes.roundingPaymentType = paymentType
    }

    if (paymentType.type_id === PAYMENT_TYPES.LOYALTY) {
      systemTypes.loyaltyPaymentType = paymentType
    }

    if (
      paymentType.type_id === PAYMENT_TYPES.LSPAY &&
      !filteredLSPay &&
      !paymentNotAvailable(paymentType)
    ) {
      filteredLSPay = true
      systemTypes.lspayPaymentType = paymentType
    }
  }

  return systemTypes
}

export type SystemPaymentType = {
  roundingPaymentType?: ApiRetailerPaymentType
  currencyAdjustmentPaymentType?: ApiRetailerPaymentType
  storeCreditPaymentType?: ApiRetailerPaymentType
  loyaltyPaymentType?: ApiRetailerPaymentType
  lspayPaymentType?: ApiRetailerPaymentType
}
/**
 * Hook to fetch payment types this should split the payments into two groups
 * 1. System payment types (store credit, loyalty, on account, etcx), these are used for customer payment options
 * 2. All payment types by filtering out the ones that are not needed
 * ***NOTES***: in case of indexDB closed connection, it will catch the error and return an empty payment just like original behavior
 */
export const useFetchPaymentTypes = () => {
  const [filteredPaymentTypes, setFilteredPaymentTypes] = useState<
    ApiRetailerPaymentType[]
  >([])
  const trainingModeManager = useTrainingModeManager()
  const [
    systemPaymentTypes,
    setSystemPaymentTypes,
  ] = useState<SystemPaymentType>({})

  const sale = useAppSelector(selectCurrentSale)

  // Determine if this is an LS Pay ecommerce refund
  const isLSPayEcomRefund =
    getFeatureFromCache('lspay_eseries_refunds_on_xseries') &&
    isReturnedLSPayESale(sale)

  const fetchPaymentTypes = useCallback(async () => {
    // Get all payment types
    const fetchedPaymentTypes = await paymentTypesStore.all().catch(err => {
      trackError(err)
      return []
    })

    // Set system payment types first
    const systemTypes = defineSystemPaymentTypes(fetchedPaymentTypes)
    setSystemPaymentTypes(systemTypes)

    let processedPaymentTypes = fetchedPaymentTypes
    if (isLSPayEcomRefund) {
      processedPaymentTypes = preparePaymentTypesForLSpayEcomRefund(
        fetchedPaymentTypes
      )
    }

    const filteredPaymentTypes = filterPaymentTypesForDisplay(
      processedPaymentTypes,
      isLSPayEcomRefund,
      trainingModeManager.trainingModeEnabled
    )

    setFilteredPaymentTypes(filteredPaymentTypes)
  }, [isLSPayEcomRefund, trainingModeManager.trainingModeEnabled])

  useEffect(() => {
    fetchPaymentTypes()
  }, [fetchPaymentTypes])

  return {
    filteredPaymentTypes,
    systemPaymentTypes,
    fetchPaymentTypes,
  }
}
